import React, {FC} from 'react';
import { InvoiceTransactionVO } from "../../../types/model/valueObject/invoice/InvoiceTransactionVO";
import { formatCurrencyValue } from "../../../util/PaymentUtils";
import {BookingTransactionStatusType, BookingTransactionTypeLabel} from "../../constants/securspace-constants";
import { BookingTransactionStatus } from "../../constants/booking-transaction-constants";
import {
  CollapsableCardBodyContainer,
  CollapsableCardClickableContainer,
  CollapsableCardCollapseContainer,
  CollapsableCardContainer,
  CollapsableCardExpandIconButton,
  CollapsableCardHeaderActionsContainer,
  CollapsableCardHeaderContainer,
  CollapsableCardHeaderItem,
  CollapsableCardBodyItem
} from "../../cardComponents";

type SupplierInvoicesCardListItemProps = {
  item: InvoiceTransactionVO;
  expanded?: boolean;
  onToggleExpand: () => void;
}

const TRANSFER_TYPE_LABELS: Record<string, string> = {
  CHARGE: 'Payment',
  REFUND: 'Refund',
  FEE: 'Fee',
};

/**
 * Component that displays all information for a single supplier invoice item
 * @param item the InvoiceTransactionVO to display
 * @param expanded
 * @param onToggleExpand
 * @constructor
 */
const SupplierInvoicesCardListItem: FC<SupplierInvoicesCardListItemProps> = ({
  item,
  expanded,
  onToggleExpand
}) => {

  const isManualPaymentCharge =
    item?.status === BookingTransactionStatusType.MANUAL_PAYMENT && item?.transferType === 'CHARGE';

  return (
    <CollapsableCardContainer expanded={expanded}>
      <CollapsableCardClickableContainer onClick={onToggleExpand}>
        <CollapsableCardHeaderContainer>
          <CollapsableCardHeaderItem value={item?.transactionNumber} label={'Invoice Number'} />
          <CollapsableCardHeaderItem value={formatCurrencyValue(item?.supplierAmount, true)} label={'Amount'} />
          <CollapsableCardHeaderItem value={(BookingTransactionTypeLabel as Record<string, string>)[String(item?.transactionType)] ?? item?.transactionType} label={'Invoice Type'} />
          <CollapsableCardHeaderItem value={item?.serviceDates} label={'Service Dates'} />
          <CollapsableCardHeaderItem value={item?.locationName} label={'Location'} />
          <CollapsableCardHeaderItem value={item?.buyerCompanyName} label={'Buyer'} />
          <CollapsableCardHeaderActionsContainer>
            <CollapsableCardExpandIconButton expanded={expanded ?? false} />
          </CollapsableCardHeaderActionsContainer>
        </CollapsableCardHeaderContainer>
      </CollapsableCardClickableContainer>
      <CollapsableCardCollapseContainer in={expanded}>
        <CollapsableCardBodyContainer>
          <CollapsableCardBodyItem value={TRANSFER_TYPE_LABELS[String(item?.transferType)] ?? item?.transferType} label={'Type of Charge'} />
          <CollapsableCardBodyItem value={item?.numberOfSpaces} label={'Spaces'} />
          <CollapsableCardBodyItem value={BookingTransactionStatus[item?.status]?.supplierInvoiceStatusGroup?.label ?? item?.status} label={'Status'} />
          <CollapsableCardBodyItem value={item?.createdOn} label={'Invoice Created Date'} />
          <CollapsableCardBodyItem value={item?.invoiceAge} label={'Age (Days)'} />
          <CollapsableCardBodyItem value={item?.paymentCreatedOn} label={'Payment Initiated Date'} />

          <CollapsableCardBodyItem value={isManualPaymentCharge ? item?.manualPaymentCompletedOn : item?.paymentCompletedOn} label={'Process Completed Date'} />
          <CollapsableCardBodyItem value={item?.payoutCreatedOn} label={'Payout Initiated'} />
          <CollapsableCardBodyItem value={item?.payoutCompletedOn} label={'Payout Completed'} />
          <CollapsableCardBodyItem value={item?.paymentType} label={'Payment Type'} />
          <CollapsableCardBodyItem value={item?.createdOn} label={'Charged Date'} />
          <CollapsableCardBodyItem value={item?.bookingNumber} label={'Booking'} />

          <CollapsableCardBodyItem value={item?.assetType} label={'Equipment Type'} />
          <CollapsableCardBodyItem value={item?.serviceFeeType} label={'Service Charge Type'} />
        </CollapsableCardBodyContainer>
      </CollapsableCardCollapseContainer>
    </CollapsableCardContainer>
  );
};

export { SupplierInvoicesCardListItem };
